/**
 * tenant-url — cross-subdomain URL builder (F7b T7b.3).
 *
 * Buduje URL do tenant scope (`<slug>.app.<base>/path?query`) z aktualnego hostname.
 *
 * PROD (`app.naszsaas.pl`) → `https://<slug>.app.naszsaas.pl/login?email=x`.
 * DEV (`localhost`, `app.localhost`) → same-origin z DEV override:
 *   `http://localhost:5173/login?email=x&hostScope=tenant&__devSlug=<slug>&__devTenantId=<uuid>`.
 * Host-resolver DEV akceptuje `?hostScope=tenant` (patrz `hooks/useHostScope.ts`);
 * `__devSlug` oznacza frontowo do jakiego tenanta user chciałby; `__devTenantId` (opcjonalny,
 * F7b T7b.9) jest konsumowany przez `lib/dev-tenant-id.ts` — daje świeżo provisioned tenant
 * możliwość wstrzyknięcia swojego uuid do axios interceptora bez restart Vite. PROD ignoruje
 * query — cross-subdomain host resolver załatwia routing.
 */

export interface BuildTenantLoginUrlOptions {
  slug: string;
  email?: string;
  hostname?: string;
  protocol?: string;
  port?: string;
  /**
   * Opcjonalny tenant UUID. W DEV (`localhost` fallback) dołączany jako `&__devTenantId=`
   * do URL żeby odbiorca (po SPA reload) zapisał go do sessionStorage (patrz
   * `lib/dev-tenant-id.ts`). W PROD ignorowany.
   */
  tenantId?: string;
}

export function buildTenantLoginUrl(opts: BuildTenantLoginUrlOptions): string {
  const hostname = opts.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const protocol =
    opts.protocol ?? (typeof window !== 'undefined' ? window.location.protocol : 'https:');
  const port = opts.port ?? (typeof window !== 'undefined' ? window.location.port : '');

  const params = new URLSearchParams();
  if (opts.email) params.set('email', opts.email);

  const appMatch = hostname.match(/^app\.(.+)$/i);
  if (appMatch) {
    const base = appMatch[1];
    const portSuffix = port ? `:${port}` : '';
    const query = params.toString() ? `?${params.toString()}` : '';
    return `${protocol}//${opts.slug}.app.${base}${portSuffix}/login${query}`;
  }

  // DEV / fallback: same-origin z DEV override hook-a.
  params.set('hostScope', 'tenant');
  params.set('__devSlug', opts.slug);
  if (opts.tenantId) params.set('__devTenantId', opts.tenantId);
  return `/login?${params.toString()}`;
}

/**
 * ADR-0075 — URL wejścia do firmy przez handoff (multitenant "wspólne konto"). Po
 * centralnym logowaniu / wyborze / przełączeniu klucz handoff idzie w URL-fragmencie
 * (`#__handoffKey=`), a `main.tsx` (pickupHandoffKey) wymienia go na sesję i ląduje w
 * shellu firmy (path `/`, nie `/login`).
 *
 * PROD: `https://<slug>.app.<base>/#__handoffKey=<key>`.
 * DEV:  `/?hostScope=tenant&__devSlug=<slug>&__devTenantId=<uuid>#__handoffKey=<key>`
 *       (`__devTenantId` daje pickupHandoffKey X-Tenant-Id do redeemu; hostScope ustawia
 *       scope tenant w useHostScope).
 */
/**
 * ADR-0075 — URL centralnego logowania (public scope) = ekran z wyborem firmy.
 * Wołane z tenant scope (logout / redirect z per-tenant loginu), żeby KAŻDE logowanie
 * szło przez picker, nie przez logowanie per-subdomena.
 *
 * PROD (na `<slug>.app.<base>`): `https://app.<base>/login`.
 * DEV (localhost): `/login?hostScope=public` (query wygrywa nad sticky sessionStorage scope).
 */
export function buildCentralLoginUrl(opts?: {
  hostname?: string;
  protocol?: string;
  port?: string;
  /** Opcjonalny powód (np. `handoff-failed`) → `?reason=` na ekranie logowania (notka). */
  reason?: string;
}): string {
  const hostname =
    opts?.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const protocol =
    opts?.protocol ?? (typeof window !== 'undefined' ? window.location.protocol : 'https:');
  const port = opts?.port ?? (typeof window !== 'undefined' ? window.location.port : '');

  // `<slug>.app.<base>` → `app.<base>`; `app.<base>` → bez zmian.
  const tenantMatch = hostname.match(/^[^.]+\.app\.(.+)$/i);
  if (tenantMatch) {
    const base = tenantMatch[1];
    const portSuffix = port ? `:${port}` : '';
    const query = opts?.reason ? `?reason=${encodeURIComponent(opts.reason)}` : '';
    return `${protocol}//app.${base}${portSuffix}/login${query}`;
  }
  // DEV / fallback: same-origin, wymuś scope public (query > sticky sessionStorage).
  const params = new URLSearchParams({ hostScope: 'public' });
  if (opts?.reason) params.set('reason', opts.reason);
  return `/login?${params.toString()}`;
}

/**
 * URL startu sesji impersonacji (super admin → panel firmy). Klucz jednorazowy idzie w
 * fragmencie (`#__impSessionKey=`), a `main.tsx` (pickupImpersonationKey) wymienia go na
 * token i ląduje w shellu firmy (path `/`). NIE `/login` — od ADR-0075 ta strona
 * przekierowuje na centralne logowanie i sesja impersonacji nigdy by się nie pokazała.
 *
 * PROD: `https://<slug>.app.<base>/#__impSessionKey=<key>`.
 * DEV:  `/?hostScope=tenant&__devSlug=<slug>&__devTenantId=<uuid>#__impSessionKey=<key>`.
 */
export function buildTenantImpersonationUrl(opts: {
  slug: string;
  tenantId: string;
  sessionKey: string;
  hostname?: string;
  protocol?: string;
  port?: string;
}): string {
  const hostname = opts.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const protocol =
    opts.protocol ?? (typeof window !== 'undefined' ? window.location.protocol : 'https:');
  const port = opts.port ?? (typeof window !== 'undefined' ? window.location.port : '');
  const fragment = `#__impSessionKey=${encodeURIComponent(opts.sessionKey)}`;

  // Impersonację startuje super admin z `admin.<base>` (panel ITE), nie z `app.<base>` —
  // dlatego, w odróżnieniu od pozostałych builderów, akceptujemy oba prefiksy.
  const appMatch = hostname.match(/^(?:app|admin)\.(.+)$/i);
  if (appMatch) {
    const base = appMatch[1];
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${opts.slug}.app.${base}${portSuffix}/${fragment}`;
  }

  const params = new URLSearchParams();
  params.set('hostScope', 'tenant');
  params.set('__devSlug', opts.slug);
  params.set('__devTenantId', opts.tenantId);
  return `/?${params.toString()}${fragment}`;
}

export function buildTenantHandoffUrl(opts: {
  slug: string;
  tenantId: string;
  handoffKey: string;
  hostname?: string;
  protocol?: string;
  port?: string;
}): string {
  const hostname = opts.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const protocol =
    opts.protocol ?? (typeof window !== 'undefined' ? window.location.protocol : 'https:');
  const port = opts.port ?? (typeof window !== 'undefined' ? window.location.port : '');
  const fragment = `#__handoffKey=${encodeURIComponent(opts.handoffKey)}`;

  const appMatch = hostname.match(/^app\.(.+)$/i);
  if (appMatch) {
    const base = appMatch[1];
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${opts.slug}.app.${base}${portSuffix}/${fragment}`;
  }

  // DEV / fallback: same-origin, scope tenant + dev tenant-id.
  const params = new URLSearchParams();
  params.set('hostScope', 'tenant');
  params.set('__devSlug', opts.slug);
  params.set('__devTenantId', opts.tenantId);
  return `/?${params.toString()}${fragment}`;
}
